import React, { Fragment, Suspense } from 'react';

import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import DropdownV3 from '@zola/zola-ui/src/components/Form/dropdownV3/DropdownV3';
import { ChevronDownIcon } from '@zola/zola-ui/src/components/SvgIconsV3/ChevronDown';

import Sticky from 'react-stickynode';
import type { WFacetView } from '@zola/svc-web-api-ts-client';
import { UserContext } from '@/actions/UserActions.type';
import Media from 'react-media';

// @ts-expect-error types not yet defined
import RenderDropdownSelect from '@zola/component-shopping/src/components/common/form/DropDownSelect';
// @ts-expect-error types not yet defined
import ShopSimpleHeader from '@zola/component-shopping/src/components/shop/BaseShopContainer/ShopSimpleHeader';
// @ts-expect-error types not yet defined
import CommonMobileFilter from '@zola/component-shopping/src/components/common/shop/ShopFilters/CommonShopFilters/CommonMobileFilter/CommonMobileFilter';
// @ts-expect-error types not yet defined
import CommonMobileSortMenu from '@zola/component-shopping/src/components/common/shop/ShopFilters/CommonShopFilters/CommonMobileSortMenu/CommonMobileSortMenu';
import {
  DEFAULT_SORT_OPTION,
  MEDIA_SCREEN_SIZES,
  SORT_OPTIONS,
  SORT_OPTIONS_V3,
} from '../../constants';
import { notDesktop } from '../../../../lib/responsive';

const MobileFilterScreen = ({
  facets,
  productTotal,
  selectedFilters,
  selectedSortValue,
  toggleMobileFilters,
  handleSortValueChange,
  handleClearAll,
  handleFilterSelect,
  setFilters,
}: {
  facets: WFacetView[];
  productTotal: number;
  selectedFilters: Record<string, Record<string, boolean>>;
  selectedSortValue: string;
  toggleMobileFilters: () => void;
  handleSortValueChange: (value: string) => void;
  handleClearAll: () => void;
  handleFilterSelect: (newFilters: Record<string, Record<string, boolean>>) => void;
  setFilters: (newFilters: Record<string, Record<string, boolean>>) => void;
}) => {
  const sortValue = selectedSortValue || DEFAULT_SORT_OPTION;
  const isMobileDevice = notDesktop();

  return (
    <CommonMobileFilter
      toggleMobileFilters={toggleMobileFilters}
      onMenuSelect={handleSortValueChange}
      selectedSortValue={sortValue}
      productTotal={productTotal}
      sortOptions={SORT_OPTIONS}
      onClearAll={handleClearAll}
      facets={facets}
      isMobile={isMobileDevice}
      onFilterSelect={handleFilterSelect}
      selectedFilters={selectedFilters}
      setFilters={setFilters}
    />
  );
};

const MobileSortMenu = ({
  useGridTiles,
  userContext,
  selectedFilters,
  facets,
  productTotal,
  toggleMobileFilters,
}: {
  useGridTiles?: boolean;
  userContext: UserContext | null;
  selectedFilters: Record<string, Record<string, boolean>>;
  facets: WFacetView[];
  productTotal: number;
  toggleMobileFilters: () => void;
}) => {
  const filterArray = facets
    .map(({ key, options }) =>
      (options || []).filter(
        ({ value }) =>
          selectedFilters[key as string] && selectedFilters[key as string][value as string]
      )
    )
    .map((i) => i.length);
  const filtersCount = filterArray.reduce((a, b) => a + b, 0);

  if (productTotal !== 0) {
    return (
      <Sticky
        top={56}
        innerZ={10}
        activeClass="active-filter-sticky"
        // @ts-expect-error This was here prior to refactoring and converting to TS, let as is
        className="default-sticky-filter-container"
      >
        <CommonMobileSortMenu
          onToggleMobileFilters={toggleMobileFilters}
          filtersCount={filtersCount}
          redesign={useGridTiles}
        />
      </Sticky>
    );
  }

  return <Fragment />;
};

type HeadersWithoutControlsProps = {
  customTitle?: string;
  productTotal: number | null;
};

const HeadersWithoutControls = ({ productTotal, customTitle }: HeadersWithoutControlsProps) => {
  return (
    <Suspense fallback={<Fragment />}>
      <ShopSimpleHeader productTotal={productTotal} customTitle={customTitle} />
    </Suspense>
  );
};

type HeaderControlProps = {
  newFilteringTitle: string | null;
  useGridTiles?: boolean;
  areMobileFiltersOpen: boolean;
  productTotal: number;
  selectedSortValue: string;
  facets: WFacetView[];
  handleSortValueChange: (value: string) => void;
  selectedFilters: Record<string, Record<string, boolean>>;
  toggleMobileFilters: () => void;
  handleClearAll: () => void;
  handleFilterSelect: (newFilters: Record<string, Record<string, boolean>>) => void;
  setFilters: (newFilters: Record<string, Record<string, boolean>>) => void;
  userContext: UserContext | null;
};
const HeaderControls = ({
  newFilteringTitle,
  useGridTiles,
  areMobileFiltersOpen,
  productTotal,
  selectedSortValue,
  facets,
  handleSortValueChange,
  selectedFilters,
  toggleMobileFilters,
  handleClearAll,
  handleFilterSelect,
  setFilters,
  userContext,
}: HeaderControlProps) => {
  const total = productTotal === 0 ? 'No' : productTotal;
  const localNumber = total && total.toLocaleString();
  const totalProductsDisplay = total === 1 ? `${localNumber} Item` : `${localNumber} Items`;
  const isSortVisible = productTotal > 1;
  const activeSort =
    SORT_OPTIONS_V3.find((option) => option.value === selectedSortValue) || SORT_OPTIONS_V3[0];

  const { label: sortLabel, value: sortValue } = activeSort;

  return (
    <div className="zola-shop__product-controls">
      <div className="zola-shop__product-controls__title">
        <div className="zola-shop__category-title">
          {`Explore all ${newFilteringTitle?.toLowerCase() || ''}`}
        </div>
        {total !== null && <div className="zola-shop__product-count">{totalProductsDisplay}</div>}
      </div>
      <Media query={`(max-width: ${MEDIA_SCREEN_SIZES.MOBILE_MAX})`}>
        <>
          {facets && areMobileFiltersOpen && (
            <MobileFilterScreen
              facets={facets}
              productTotal={productTotal}
              selectedFilters={selectedFilters}
              selectedSortValue={selectedSortValue}
              toggleMobileFilters={toggleMobileFilters}
              handleSortValueChange={handleSortValueChange}
              handleClearAll={handleClearAll}
              handleFilterSelect={handleFilterSelect}
              setFilters={setFilters}
            />
          )}
          <MobileSortMenu
            useGridTiles={useGridTiles}
            userContext={userContext}
            selectedFilters={selectedFilters}
            facets={facets}
            productTotal={productTotal}
            toggleMobileFilters={toggleMobileFilters}
          />
        </>
      </Media>
      {isSortVisible && (
        <Media query={`(min-width: ${MEDIA_SCREEN_SIZES.DESKTOP_MIN})`}>
          <div className="base-shop-container__button--rectangle">
            {useGridTiles ? (
              <DropdownV3
                id="shop-sort"
                name="sort"
                options={SORT_OPTIONS_V3}
                onSelect={handleSortValueChange}
                value={sortValue}
                trigger={
                  <ButtonV3 className="zola-shop__product-controls__sort">
                    Sort by {(sortLabel || '').toLowerCase()}&nbsp;
                    <ChevronDownIcon />
                  </ButtonV3>
                }
              />
            ) : (
              <RenderDropdownSelect
                id="shop-sort"
                label="Sort by"
                options={SORT_OPTIONS}
                input={{
                  value: sortValue,
                  name: 'Featured',
                  onChange: handleSortValueChange,
                }}
                showToggleAnimation
                hideEmpty
                meta={{ touched: false }}
              />
            )}
          </div>
        </Media>
      )}
    </div>
  );
};

export const PageControls = (
  props: HeadersWithoutControlsProps & HeaderControlProps & { hasNoSearchResults: boolean }
) => {
  const {
    areMobileFiltersOpen,
    customTitle,
    facets,
    handleClearAll,
    handleFilterSelect,
    handleSortValueChange,
    hasNoSearchResults,
    newFilteringTitle,
    productTotal,
    selectedFilters,
    selectedSortValue,
    setFilters,
    toggleMobileFilters,
    useGridTiles,
    userContext,
  } = props;

  if (hasNoSearchResults) {
    return <HeadersWithoutControls productTotal={productTotal} customTitle={customTitle} />;
  }
  return (
    <HeaderControls
      newFilteringTitle={newFilteringTitle}
      useGridTiles={useGridTiles}
      areMobileFiltersOpen={areMobileFiltersOpen}
      productTotal={productTotal}
      selectedSortValue={selectedSortValue}
      facets={facets}
      handleSortValueChange={handleSortValueChange}
      selectedFilters={selectedFilters}
      toggleMobileFilters={toggleMobileFilters}
      handleClearAll={handleClearAll}
      handleFilterSelect={handleFilterSelect}
      setFilters={setFilters}
      userContext={userContext}
    />
  );
};
